import React, { useEffect, useRef, useCallback, useState } from 'react';
import './ImagePlayerModal.css';
import { API_BASE_URL } from '../../../../API/api';

const ImagePlayerModal = ({ isOpen, onClose, file }) => {
  const imageRef = useRef(null);
  const modalContentRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const presignedUrlCache = useRef(new Map()); // Cache presigned URLs

  useEffect(() => {
    if (!isOpen || !file || !imageRef.current) return;

    // Collapse sidebar when image modal opens to give more space
    const sidebarToggle = document.querySelector('.sidebar-toggle');
    if (sidebarToggle) {
      const sidebar = document.querySelector('.sidebar');
      const isSidebarCollapsed = sidebar?.classList.contains('collapsed');
      // Only collapse if sidebar is not already collapsed
      if (!isSidebarCollapsed) {
        sidebarToggle.click();
      }
    }

    // Reset state when modal opens
    setIsLoading(true);
    setImageError(false);
    setImageLoaded(false);
    
    // Reset modal size to initial size
    if (modalContentRef.current) {
      modalContentRef.current.style.width = '800px';
      modalContentRef.current.style.height = '600px';
    }

    // Check cache first for faster loading
    const cacheKey = `${file.id}_${file.projectId || 'no-project'}`;
    const cachedUrl = presignedUrlCache.current.get(cacheKey);
    
    // Fetch direct S3 URL (presigned) for fast loading
    const fetchDirectUrl = async () => {
      try {
        // Use cached URL if available (presigned URLs are valid for 1 hour)
        let imageUrl = cachedUrl;
        
        if (!imageUrl) {
          const directUrlEndpoint = `${API_BASE_URL}/api/files/${file.id}/direct-url${file.projectId ? `?project_id=${file.projectId}` : ''}`;
          console.log('[ImagePlayer] Fetching presigned URL:', directUrlEndpoint);
          
          const response = await fetch(directUrlEndpoint);
          if (!response.ok) {
            throw new Error(`Failed to get presigned URL: ${response.status}`);
          }
          
          const data = await response.json();
          imageUrl = data.url;
          
          // Cache the presigned URL (valid for 1 hour)
          presignedUrlCache.current.set(cacheKey, imageUrl);
          console.log('[ImagePlayer] Got presigned S3 URL, cached for reuse');
        } else {
          console.log('[ImagePlayer] Using cached presigned URL');
        }
        
        // Create a new image to preload
        const img = new Image();
        
        // Set a maximum loading timeout of 300ms for presigned URLs (they should be instant from S3)
        const loadingTimeout = setTimeout(() => {
          console.log('[ImagePlayer] Loading timeout reached, hiding spinner');
          setIsLoading(false);
        }, 300);
        
        img.onload = () => {
          console.log('[ImagePlayer] Image loaded successfully from S3');
          clearTimeout(loadingTimeout);
          setIsLoading(false);
          setImageError(false);
          setImageLoaded(true);
          
          // Adjust modal size to fit image after it loads
          if (imageRef.current && modalContentRef.current) {
            const img = imageRef.current;
            const modal = modalContentRef.current;
            const headerHeight = 50; // Approximate header height
            
            // Get max dimensions from overlay constraints
            const maxWidth = window.innerWidth * 0.95;
            const maxHeight = window.innerHeight * 0.9;
            const maxImageHeight = maxHeight - headerHeight;
            
            // Calculate image dimensions respecting max constraints
            let imageWidth = img.naturalWidth;
            let imageHeight = img.naturalHeight;
            
            // Scale down if image exceeds max dimensions while maintaining aspect ratio
            if (imageWidth > maxWidth || imageHeight > maxImageHeight) {
              const widthRatio = maxWidth / imageWidth;
              const heightRatio = maxImageHeight / imageHeight;
              const scale = Math.min(widthRatio, heightRatio);
              
              imageWidth = imageWidth * scale;
              imageHeight = imageHeight * scale;
            }
            
            // Set modal size to fit image (with header)
            modal.style.width = `${imageWidth}px`;
            modal.style.height = `${imageHeight + headerHeight}px`;
          }
        };

        img.onerror = () => {
          console.error('[ImagePlayer] Error loading image from S3, trying fallback');
          clearTimeout(loadingTimeout);
          // Fallback to download endpoint if direct URL fails
          const fallbackUrl = `${API_BASE_URL}/api/files/${file.id}/download${file.projectId ? `?project_id=${file.projectId}` : ''}`;
          const fallbackImg = new Image();
          fallbackImg.onload = () => {
            setIsLoading(false);
            setImageError(false);
            setImageLoaded(true);
            
            // Adjust modal size to fit image after it loads
            if (imageRef.current && modalContentRef.current) {
              const img = imageRef.current;
              const modal = modalContentRef.current;
              const headerHeight = 50;
              
              const maxWidth = window.innerWidth * 0.95;
              const maxHeight = window.innerHeight * 0.9;
              const maxImageHeight = maxHeight - headerHeight;
              
              let imageWidth = img.naturalWidth;
              let imageHeight = img.naturalHeight;
              
              if (imageWidth > maxWidth || imageHeight > maxImageHeight) {
                const widthRatio = maxWidth / imageWidth;
                const heightRatio = maxImageHeight / imageHeight;
                const scale = Math.min(widthRatio, heightRatio);
                
                imageWidth = imageWidth * scale;
                imageHeight = imageHeight * scale;
              }
              
              modal.style.width = `${imageWidth}px`;
              modal.style.height = `${imageHeight + headerHeight}px`;
            }
          };
          fallbackImg.onerror = () => {
            setIsLoading(false);
            setImageError(true);
          };
          fallbackImg.src = fallbackUrl;
          imageRef.current.src = fallbackUrl;
        };

        // Set the source to start loading immediately
        img.src = imageUrl;
        imageRef.current.src = imageUrl;
        
      } catch (error) {
        console.error('[ImagePlayer] Error fetching direct URL, using fallback:', error);
        // Fallback to download endpoint
        const fallbackUrl = `${API_BASE_URL}/api/files/${file.id}/download${file.projectId ? `?project_id=${file.projectId}` : ''}`;
        const img = new Image();
        
        // Set a maximum loading timeout of 1 second for fallback too
        const fallbackTimeout = setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        
        img.onload = () => {
          clearTimeout(fallbackTimeout);
          setIsLoading(false);
          setImageError(false);
          setImageLoaded(true);
          
          // Adjust modal size to fit image after it loads
          if (imageRef.current && modalContentRef.current) {
            const img = imageRef.current;
            const modal = modalContentRef.current;
            const headerHeight = 50;
            const totalHeight = img.naturalHeight + headerHeight;
            const totalWidth = img.naturalWidth;
            
            modal.style.width = `${Math.min(totalWidth, window.innerWidth * 0.95)}px`;
            modal.style.height = `${Math.min(totalHeight, window.innerHeight * 0.9)}px`;
          }
        };

        img.onerror = () => {
          clearTimeout(fallbackTimeout);
          setIsLoading(false);
          setImageError(true);
        };

        img.src = fallbackUrl;
        imageRef.current.src = fallbackUrl;
      }
    };

    fetchDirectUrl();

    // Cleanup
    return () => {
      setIsLoading(true);
      setImageError(false);
    };
  }, [isOpen, file]);

  // Handle modal close
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleClose]);

  if (!isOpen || !file) return null;

  return (
    <div className="image-player-modal-overlay" onClick={handleClose}>
      <div 
        ref={modalContentRef}
        className="image-player-modal-content" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="image-player-modal-header">
          <h3 className="image-player-modal-title">{file.name}</h3>
          <button className="image-player-modal-close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="image-player-modal-body">
          {isLoading && (
            <div className="image-loading-overlay">
              <div className="image-loading-spinner"></div>
              <p className="image-loading-text">Loading image...</p>
            </div>
          )}
          {imageError && (
            <div className="image-error-overlay">
              <i className="fas fa-exclamation-triangle"></i>
              <p className="image-error-text">Failed to load image</p>
            </div>
          )}
          <div className="image-container">
            <img
              ref={imageRef}
              className="image-display"
              alt={file.name}
              style={{ display: isLoading || imageError ? 'none' : 'block' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImagePlayerModal;
